import { selectShouldCleanAuthParams, useAuthStore } from '@/store/authStore';
import { useGo, useParsed } from '@refinedev/core';
import queryString from 'query-string';
import { useEffect } from 'react';

/**
 * Hook to clean up authentication-related query parameters using useGo
 * Automatically removes auth params (token, jwt) from URL after authentication
 *
 * This hook monitors the shouldCleanAuthParams flag from the auth store and
 * removes sensitive authentication parameters (token, jwt, originalUserRole)
 * from the URL. The flag is only set when token or jwt params are processed
 * by authProvider.check().
 *
 * @example
 * ```tsx
 * function App() {
 *   useCleanAuthQueryParams();
 *   // ... rest of app
 * }
 * ```
 */
export const useCleanAuthQueryParams = () => {
  const go = useGo();
  const { pathname } = useParsed();
  const shouldCleanAuthParams = useAuthStore(selectShouldCleanAuthParams);

  useEffect(() => {
    // Only clean params when the flag is set (i.e., when token or jwt were in URL)
    if (!shouldCleanAuthParams) {
      return;
    }

    const params = queryString.parse(location.search);

    const paramsToRemove = ['token', 'jwt', 'originalUserRole'];
    const hasParamsToRemove = paramsToRemove.some((param) => params?.[param]);

    if (hasParamsToRemove) {
      // Create new params object without the auth params and route params
      const cleanedParams = { ...params };
      paramsToRemove.forEach((param) => {
        delete cleanedParams[param];
      });

      console.log('Cleaning auth query params:', cleanedParams);
      try {
        // Use go to update the URL without the auth params
        go({
          to: pathname ?? '/',
          query: cleanedParams,
          type: 'replace',
        });
      } catch (error) {
        console.error('Failed to clean auth query params:', error);
      }
    }
  }, [shouldCleanAuthParams, go]);
};
